import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useStore } from '@tanstack/react-form';
import {
  Accordion,
  AccordionItem,
  Button,
  Checkbox,
  DatePicker,
  DatePickerInput,
  IconButton,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TextInput,
} from '@carbon/react';
import {
  Add,
  Copy,
  Draggable,
  Save,
  SidePanelClose,
  SidePanelOpen,
  TrashCan,
} from '@carbon/react/icons';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { PageHeader } from 'components/page-header';
import { FormattedCount } from 'components/formatted-count';
import {
  ActivityFeedPanel,
  ActivityFeedPanelOpenButton,
} from 'components/activity-feed-panel';
import { usePricingEnums } from 'pages/pricing/shared/use-pricing-enums';
import { NumericField } from 'pages/pricing/shared/numeric-field';
import type {
  CurveOption,
  Transformation,
  TransformationStep,
} from 'pages/pricing/shared/types';
import styles from './transformation-editor.module.scss';

// The editor works in a simplified "operation" shape (scope All/Before/After/Between, anchor
// Fixed/Relative); it maps to/from the richer TransformationStep at the save boundary. A fixed
// anchor uses the date fields; a relative anchor uses the month offsets from the as-of. The primary
// bound lives in `date`/`months`: for `after` it's the start, for `before` it's the end. `between`
// carries BOTH bounds — its start in `date`/`months`, its end in `dateEnd`/`monthsEnd`.
export interface OpRow {
  id: string;
  curveId: number;
  operation: string; // adder / multiplier / fixed
  value: number;
  applyToSettle: boolean; // also apply this operation to the settled/realized leg
  scope: string; // all / before / after / between  (→ step.tenorType)
  anchor: string; // fixed / relative                (→ step.anchorMode)
  date: string; // YYYY-MM-DD  (fixed: after-start / before-end / between-start)
  months: number; // months from the as-of  (relative: after-start / before-end / between-start)
  dateEnd: string; // YYYY-MM-DD  (fixed: between-end)
  monthsEnd: number; // months from the as-of  (relative: between-end)
}

interface TxFormData {
  name: string;
  ops: OpRow[];
}

let seq = 0;
const nextId = () => {
  seq += 1;
  return `op-${seq}`;
};

const isoOf = (d?: Date) => {
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    '0'
  )}-${String(d.getDate()).padStart(2, '0')}`;
};
const fmtDisplayDate = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
      });
};

// ── step ⇄ op mapping ─────────────────────────────────────────────
export const stepToOp = (s: TransformationStep): OpRow => {
  const scope = s.tenorType; // all / before / after / between — preserved verbatim (no coercion)
  const relative = s.anchorMode === 'relative';
  // Fixed anchors carry calendar dates; relative anchors carry month offsets. Read whichever this
  // step uses for its scope; unused bounds stay at their empty default. `between` reads BOTH bounds.
  let date = '';
  let months = 0;
  let dateEnd = '';
  let monthsEnd = 0;
  if (scope === 'after' || scope === 'between') {
    if (relative) months = s.relativeStartMonths ?? 0;
    else date = s.tenorStart ?? '';
  } else if (scope === 'before') {
    if (relative) months = s.relativeEndMonths ?? 0;
    else date = s.tenorEnd ?? '';
  }
  if (scope === 'between') {
    if (relative) monthsEnd = s.relativeEndMonths ?? 0;
    else dateEnd = s.tenorEnd ?? '';
  }
  return {
    id: nextId(),
    curveId: s.curveId,
    operation: s.operation,
    value: s.value,
    applyToSettle: s.applyToSettle ?? false,
    scope,
    anchor: s.anchorMode,
    date,
    months,
    dateEnd,
    monthsEnd,
  };
};

export const opToStep = (op: OpRow, i: number): Partial<TransformationStep> => {
  const relative = op.anchor === 'relative';
  const hasStart = op.scope === 'after' || op.scope === 'between';
  const hasEnd = op.scope === 'before' || op.scope === 'between';
  // `before` keeps its single (end) bound in date/months; `between` puts its end in dateEnd/monthsEnd.
  const endDate = op.scope === 'before' ? op.date : op.dateEnd;
  const endMonths = op.scope === 'before' ? op.months : op.monthsEnd;
  return {
    stepId: i + 1,
    curveId: op.curveId,
    operation: op.operation,
    value: op.value,
    tenorType: op.scope,
    anchorMode: op.anchor,
    tenorStart: !relative && hasStart ? op.date : null,
    tenorEnd: !relative && hasEnd ? endDate : null,
    relativeStartMonths: relative && hasStart ? op.months : null,
    relativeEndMonths: relative && hasEnd ? endMonths : null,
    applyToSettle: op.applyToSettle,
  };
};

// A curve's formula, split into the distinct tenor windows its operations carve out.
export interface CurveFormula {
  curveLabel: string;
  segments: string[]; // one per window, e.g. "(NYMEX WTI CMA + 2)[0-12 mos]"
}

const fmtVal = (v: number): string => String(v);

// Fold a window's active operations onto a base curve, IN ORDER, mirroring the backend
// TransformationApplier: adder → "+ n", multiplier → "× n", fixed → resets the running value to n
// (so a later fixed wipes out earlier terms, and adders after it stack on top). The result is the
// inner text of one "(…)" segment — the bare curve name when nothing has reset it, else a value
// expression.
export const foldExpr = (baseLabel: string, ops: OpRow[]): string => {
  let expr = baseLabel;
  ops.forEach((o) => {
    if (o.operation === 'fixed') expr = fmtVal(o.value);
    else if (o.operation === 'multiplier')
      expr = `${expr} × ${fmtVal(o.value)}`;
    else
      expr = `${expr} ${o.value < 0 ? '−' : '+'} ${fmtVal(Math.abs(o.value))}`;
  });
  return expr;
};

// A tenor window [lo, hi] in months from the as-of; hi === null is open-ended.
export type Window = [number, number | null];

// Bracket label for a window, following the mock's convention (an "after +N mos" op starts the
// next window at N+1, so its boundaries read [0-12], [13-48], [49+]).
export const windowLabel = ([lo, hi]: Window): string => {
  if (lo === 0 && hi === null) return '[all tenors]';
  if (hi === null) return `[${lo}+ mos]`;
  return `[${lo}-${hi} mos]`;
};

// Is a relative-anchored op active in a window? 'all' (and fixed-date anchors, which carry no
// resolvable month here) apply everywhere; 'after +m' starts one month past its anchor; 'before
// +m' runs through it; 'between [m,mEnd]' is the intersection (starts one month past m, runs
// through mEnd).
export const activeInWindow = (o: OpRow, [lo, hi]: Window): boolean => {
  if (o.scope === 'all' || o.anchor !== 'relative') return true;
  if (o.scope === 'after') return lo >= o.months + 1;
  if (o.scope === 'before') return hi !== null && hi <= o.months;
  if (o.scope === 'between')
    return lo >= o.months + 1 && hi !== null && hi <= o.monthsEnd;
  return true;
};

// One formula block per distinct curve (first-seen order), each split into the distinct tenor
// windows its operations define — including windows where no op is active, which fold to the bare
// curve (it passes through unchanged there). Adjacent windows that fold to the same expression are
// merged back into one range.
export const combinedFormulas = (
  ops: OpRow[],
  curveLabel: (id: number) => string
): CurveFormula[] => {
  if (ops.length === 0) return [];
  const byCurve = new Map<number, OpRow[]>();
  ops.forEach((o) => {
    const existing = byCurve.get(o.curveId);
    if (existing) existing.push(o);
    else byCurve.set(o.curveId, [o]);
  });

  return [...byCurve.entries()].map(([curveId, curveOps]) => {
    const base = curveLabel(curveId);

    // Relative after/before anchors cut the month axis; each `m` closes a window at m and opens
    // the next at m + 1. 'all' and fixed-date anchors add no cut.
    const cuts = [
      ...new Set(
        curveOps.flatMap((o) => {
          if (o.anchor !== 'relative') return [];
          if (o.scope === 'after' || o.scope === 'before') return [o.months];
          if (o.scope === 'between') return [o.months, o.monthsEnd];
          return [];
        })
      ),
    ].sort((a, b) => a - b);

    const windows: Window[] = [];
    let lo = 0;
    cuts.forEach((c) => {
      windows.push([lo, c]);
      lo = c + 1;
    });
    windows.push([lo, null]);

    // Fold each window (an untouched one folds to the bare curve); merge adjacent windows with an
    // identical expression back into one range.
    const segments: string[] = [];
    let pending: { expr: string; win: Window } | null = null;
    const flush = () => {
      if (pending)
        segments.push(`(${pending.expr})${windowLabel(pending.win)}`);
    };
    windows.forEach((win) => {
      const active = curveOps.filter((o) => activeInWindow(o, win));
      const expr = foldExpr(base, active);
      if (pending && pending.expr === expr && pending.win[1] === win[0] - 1) {
        pending.win = [pending.win[0], win[1]];
      } else {
        flush();
        pending = { expr, win };
      }
    });
    flush();

    return { curveLabel: base, segments };
  });
};

// Stable module-level array so DndContext doesn't see a new `modifiers` reference every render.
const DND_MODIFIERS = [restrictToVerticalAxis];

// A drag-to-reorder operation row. The leading "#" cell doubles as the drag handle, so the
// row's form controls stay clickable; the rest of the cells are passed through as children.
function SortableOpRow({
  id,
  index,
  children,
}: {
  id: string;
  index: number;
  children: React.ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  return (
    <TableRow
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
      }}
    >
      <TableCell className={styles.indexCell}>
        <button
          type="button"
          className={styles.dragHandle}
          aria-label="Drag to reorder operation"
          {...attributes}
          {...listeners}
        >
          <Draggable />
          <span>{String(index + 1).padStart(2, '0')}</span>
        </button>
      </TableCell>
      {children}
    </TableRow>
  );
}

interface Props {
  transformation: Transformation;
  isNew?: boolean; // an unsaved draft (the `/new` route) — create on Save, no Duplicate/Delete/Comments
  curveOptions: CurveOption[];
  onSave: (payload: Partial<Transformation>) => Promise<Transformation>;
  onDuplicate: () => void;
  onRequestDelete: () => void;
}

// The default name the list "Add" seeds a draft with — Save stays disabled until it's changed.
const isPlaceholderName = (name: string) =>
  /^New transformation(\s+\d+)?$/i.test(name.trim());

type SectionKey = 'operations' | 'formula';
const SECTIONS: { key: SectionKey; label: string }[] = [
  { key: 'operations', label: 'Operations' },
  { key: 'formula', label: 'Combined Series Formulas' },
];

export function TransformationEditor({
  transformation,
  isNew = false,
  curveOptions,
  onSave,
  onDuplicate,
  onRequestDelete,
}: Props) {
  const { enums } = usePricingEnums();
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [visible, setVisible] = useState<Record<SectionKey, boolean>>({
    operations: true,
    formula: true,
  });

  const operationOpts = enums?.operations ?? [];
  const scopeOpts = enums?.tenorTypes ?? []; // all / before / after / between
  const anchorOpts = enums?.anchorModes ?? [];
  const curveLabel = (id: number) =>
    curveOptions.find((o) => o.value === id)?.label ?? `curve #${id}`;

  // Seed the form by DERIVING defaultValues from the `transformation` (the source of truth),
  // memoized on it. Two reasons for the memo: stepToOp calls nextId(), so mapping inline would
  // mint fresh row ids every render and make the drag-and-drop droppables re-register in a loop
  // (React "maximum update depth"); and deriving-rather-than-freezing is the app's canonical form
  // pattern (see pages/packages/show/eval3-new/hooks.ts). A frozen defaultValues prop + post-save
  // form.reset(value) does NOT work: reset() mutates the form's internal defaultValues, but the
  // useForm adapter re-applies the STATIC prop on the next render and silently reverts the seed —
  // that was the "typed months snap back to the old value after Save" bug. With the prop itself
  // derived from `transformation`, a save's refresh() flows the server truth straight back in.
  const seedValues = useMemo<TxFormData>(
    () => ({
      name: transformation.name,
      ops: transformation.steps.map(stepToOp),
    }),
    [transformation]
  );
  const form = useForm({
    defaultValues: seedValues,
    onSubmit: async ({ value }) => {
      await onSave({
        name: value.name.trim() || 'Untitled',
        steps: value.ops.map(opToStep) as TransformationStep[],
      });
      // No form.reset here: onSave triggers the parent's refresh(), which hands us a new
      // `transformation` → seedValues re-derives to the saved state and the effect below
      // rebaselines the store to it. (Resetting to the submitted values fought the adapter's
      // defaultValues re-sync and left stale months on screen.)
    },
  });

  // Rebaseline to server truth after a refresh (save / duplicate). The parent replaces the
  // transformations list on refresh, so `transformation` arrives as a NEW object reference —
  // seedValues re-derives, and this snaps the store to it (clearing dirty/touched) so the display
  // matches what persisted and Save goes disabled. TanStack's defaultValues re-sync alone won't
  // do it (it refuses to clobber the fields the user just touched). A record SWITCH is handled by
  // the parent's key remount; this covers the same-record save. Skipped on mount (ref pre-seeded).
  const prevTxRef = useRef(transformation);
  useEffect(() => {
    if (prevTxRef.current === transformation) return;
    prevTxRef.current = transformation;
    form.reset(); // no args → snap to the current (derived) defaultValues
  }, [transformation, form]);

  const values = useStore(form.store, (s) => s.values);
  const { isDirty, isSubmitting } = useStore(form.store, (s) => ({
    isDirty: s.isDirty,
    isSubmitting: s.isSubmitting,
  }));

  // Save is gated on a real, non-placeholder name (the user must rename a fresh draft away from
  // "New transformation N"), plus an actual change (a new draft is savable once renamed). The
  // nameError string both blocks Save and drives the field's red-border/invalidText (mirroring the
  // Market Data view name field).
  const trimmedName = values.name.trim();
  const nameError =
    trimmedName === ''
      ? 'Enter a name to save.'
      : isPlaceholderName(trimmedName)
        ? 'Rename this transformation before saving.'
        : '';
  const canSave = nameError === '' && (isNew || isDirty) && !isSubmitting;

  const setOps = (next: OpRow[]) => form.setFieldValue('ops', next);
  const updateOp = (i: number, patch: Partial<OpRow>) =>
    setOps(values.ops.map((o, idx) => (idx === i ? { ...o, ...patch } : o)));
  const addOp = () =>
    setOps([
      ...values.ops,
      {
        id: nextId(),
        curveId: curveOptions[0]?.value ?? 0,
        operation: 'adder',
        value: 0,
        applyToSettle: false,
        scope: 'all',
        anchor: 'fixed',
        date: '',
        months: 0,
        dateEnd: '',
        monthsEnd: 0,
      },
    ]);
  const removeOp = (i: number) =>
    setOps(values.ops.filter((_, idx) => idx !== i));

  // One tenor-bound input for an op row: a month-offset field (relative anchor) or a date picker
  // (fixed anchor). `which` selects the bound — 'start' reads/writes date/months, 'end' (only a
  // `between` op has one) reads/writes dateEnd/monthsEnd.
  const renderBoundInput = (op: OpRow, i: number, which: 'start' | 'end') => {
    const relative = op.anchor === 'relative';
    const months = which === 'start' ? op.months : op.monthsEnd;
    const date = which === 'start' ? op.date : op.dateEnd;
    return relative ? (
      <NumericField
        id={`op-${which}-months-${op.id}`}
        size="sm"
        label="Months forward"
        hideLabel
        allowDecimal={false}
        allowNegative={false}
        min={0}
        placeholder="months"
        value={months}
        onValueChange={(m) =>
          updateOp(i, which === 'start' ? { months: m } : { monthsEnd: m })
        }
      />
    ) : (
      <DatePicker
        datePickerType="single"
        value={date}
        onChange={(dates: Date[]) =>
          updateOp(
            i,
            which === 'start'
              ? { date: isoOf(dates?.[0]) }
              : { dateEnd: isoOf(dates?.[0]) }
          )
        }
      >
        <DatePickerInput
          id={`op-${which}-date-${op.id}`}
          size="sm"
          labelText="Date"
          hideLabel
          placeholder="mm/dd/yyyy"
        />
      </DatePicker>
    );
  };

  // Drag-to-reorder for the operation rows. A small activation distance keeps clicks on the
  // row's form controls from starting a drag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  // Stable id list for SortableContext (same content each render, but a fresh array reference
  // would make dnd-kit re-measure needlessly).
  const opIds = useMemo(() => values.ops.map((o) => o.id), [values.ops]);
  const handleReorder = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = values.ops.findIndex((o) => o.id === active.id);
    const to = values.ops.findIndex((o) => o.id === over.id);
    if (from !== -1 && to !== -1) setOps(arrayMove(values.ops, from, to));
  };

  return (
    <div className={styles.root}>
      <PageHeader
        breadcrumbs={[
          ['/pricing/transformation-builder', 'Price Transformations'],
          [
            isNew
              ? '/pricing/transformation-builder/new'
              : `/pricing/transformation-builder/${transformation.id}`,
            values.name || (isNew ? 'New transformation' : 'Untitled'),
          ],
        ]}
        title={values.name || (isNew ? 'New transformation' : 'Untitled')}
        actions={
          <div className={styles.headerActions}>
            <Button
              kind="primary"
              size="md"
              renderIcon={Save}
              disabled={!canSave}
              onClick={() => form.handleSubmit()}
            >
              {isSubmitting ? 'Saving…' : 'Save'}
            </Button>
            {!isNew && (
              <>
                <Button
                  kind="tertiary"
                  size="md"
                  renderIcon={Copy}
                  onClick={onDuplicate}
                >
                  Duplicate
                </Button>
                <Button
                  kind="danger--tertiary"
                  size="md"
                  renderIcon={TrashCan}
                  onClick={onRequestDelete}
                >
                  Delete
                </Button>
              </>
            )}
            <span className={styles.divider} aria-hidden />
            <IconButton
              kind="tertiary"
              size="md"
              label={
                panelCollapsed ? 'Show control panel' : 'Hide control panel'
              }
              align="bottom-right"
              onClick={() => setPanelCollapsed((v) => !v)}
            >
              {panelCollapsed ? <SidePanelOpen /> : <SidePanelClose />}
            </IconButton>
            {!isNew && <ActivityFeedPanelOpenButton />}
          </div>
        }
      />

      <div className={styles.body}>
        {!panelCollapsed && (
          <aside className={styles.panel}>
            <Accordion>
              <AccordionItem title="Layout" open>
                <p className={styles.sectionsLabel}>Sections</p>
                {SECTIONS.map((s) => (
                  <Checkbox
                    key={s.key}
                    id={`section-${s.key}`}
                    labelText={s.label}
                    checked={visible[s.key]}
                    onChange={() =>
                      setVisible((p) => ({ ...p, [s.key]: !p[s.key] }))
                    }
                  />
                ))}
              </AccordionItem>
            </Accordion>
          </aside>
        )}

        <div className={styles.content}>
          <section className={styles.card}>
            <h2 className={styles.cardTitle}>Details</h2>
            <div className={styles.detailsForm}>
              <TextInput
                id="tx-name"
                labelText="Transformation name"
                value={values.name}
                onChange={(e) => form.setFieldValue('name', e.target.value)}
                invalid={nameError !== ''}
                invalidText={nameError}
              />
            </div>
            <div className={styles.auditGrid}>
              <div className={styles.auditField}>
                <span className={styles.auditLabel}>Operations</span>
                <span className={styles.auditValue}>
                  <FormattedCount value={values.ops.length} />
                </span>
              </div>
              <div className={styles.auditField}>
                <span className={styles.auditLabel}>Created by</span>
                <span className={styles.auditValue}>
                  {transformation.createdBy ?? '—'}
                </span>
              </div>
              <div className={styles.auditField}>
                <span className={styles.auditLabel}>Created</span>
                <span className={styles.auditValue}>
                  {fmtDisplayDate(transformation.createdAt)}
                </span>
              </div>
              <div className={styles.auditField}>
                <span className={styles.auditLabel}>Last updated</span>
                <span className={styles.auditValue}>
                  {fmtDisplayDate(transformation.updatedAt)}
                </span>
              </div>
            </div>
          </section>

          {visible.operations && (
            <section className={styles.card}>
              <div className={styles.cardTitleRow}>
                <h2 className={styles.cardTitle}>Operations</h2>
                <Button kind="ghost" size="sm" renderIcon={Add} onClick={addOp}>
                  Add Operation
                </Button>
              </div>

              {values.ops.length > 0 ? (
                <TableContainer className={styles.tableWrap}>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    modifiers={DND_MODIFIERS}
                    onDragEnd={handleReorder}
                  >
                    <Table size="lg">
                      <TableHead>
                        <TableRow>
                          <TableHeader className={styles.colIndex}>
                            #
                          </TableHeader>
                          <TableHeader>Curve</TableHeader>
                          <TableHeader>Operation</TableHeader>
                          <TableHeader className={styles.colValue}>
                            Value
                          </TableHeader>
                          <TableHeader>Apply to settled</TableHeader>
                          <TableHeader>Tenor</TableHeader>
                          <TableHeader>Tenor type</TableHeader>
                          <TableHeader>Date</TableHeader>
                          <TableHeader
                            className={styles.colActions}
                            aria-label="Row actions"
                          />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <SortableContext
                          items={opIds}
                          strategy={verticalListSortingStrategy}
                        >
                          {values.ops.map((op, i) => (
                            <SortableOpRow key={op.id} id={op.id} index={i}>
                              <TableCell>
                                <Select
                                  id={`op-curve-${op.id}`}
                                  size="sm"
                                  labelText="Curve"
                                  hideLabel
                                  value={op.curveId}
                                  onChange={(e) =>
                                    updateOp(i, {
                                      curveId: Number(e.target.value),
                                    })
                                  }
                                >
                                  {curveOptions.map((o) => (
                                    <SelectItem
                                      key={o.value}
                                      value={o.value}
                                      text={o.label}
                                    />
                                  ))}
                                </Select>
                              </TableCell>
                              <TableCell>
                                <Select
                                  id={`op-operation-${op.id}`}
                                  size="sm"
                                  labelText="Operation"
                                  hideLabel
                                  value={op.operation}
                                  onChange={(e) =>
                                    updateOp(i, { operation: e.target.value })
                                  }
                                >
                                  {operationOpts.map((o) => (
                                    <SelectItem
                                      key={o.value}
                                      value={o.value}
                                      text={o.label}
                                    />
                                  ))}
                                </Select>
                              </TableCell>
                              <TableCell>
                                <NumericField
                                  id={`op-value-${op.id}`}
                                  size="sm"
                                  label="Value"
                                  hideLabel
                                  value={op.value}
                                  onValueChange={(value) =>
                                    updateOp(i, { value })
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <Checkbox
                                  id={`op-apply-${op.id}`}
                                  labelText="Apply to settled"
                                  hideLabel
                                  checked={op.applyToSettle}
                                  onChange={() =>
                                    updateOp(i, {
                                      applyToSettle: !op.applyToSettle,
                                    })
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <Select
                                  id={`op-scope-${op.id}`}
                                  size="sm"
                                  labelText="Tenor"
                                  hideLabel
                                  value={op.scope}
                                  onChange={(e) =>
                                    updateOp(i, { scope: e.target.value })
                                  }
                                >
                                  {scopeOpts.map((o) => (
                                    <SelectItem
                                      key={o.value}
                                      value={o.value}
                                      text={o.label}
                                    />
                                  ))}
                                </Select>
                              </TableCell>
                              <TableCell>
                                <Select
                                  id={`op-anchor-${op.id}`}
                                  size="sm"
                                  labelText="Tenor type"
                                  hideLabel
                                  value={op.anchor}
                                  onChange={(e) =>
                                    updateOp(i, { anchor: e.target.value })
                                  }
                                >
                                  {anchorOpts.map((o) => (
                                    <SelectItem
                                      key={o.value}
                                      value={o.value}
                                      text={o.label}
                                    />
                                  ))}
                                </Select>
                              </TableCell>
                              <TableCell>
                                {op.scope === 'all' ? (
                                  <span className={styles.muted}>—</span>
                                ) : (
                                  // Relative tenors edit as month offsets; fixed as calendar dates.
                                  // `between` shows both a start and an end bound.
                                  <div className={styles.tenorBounds}>
                                    {renderBoundInput(op, i, 'start')}
                                    {op.scope === 'between' && (
                                      <>
                                        <span
                                          className={styles.tenorBoundSep}
                                          aria-hidden
                                        >
                                          –
                                        </span>
                                        {renderBoundInput(op, i, 'end')}
                                      </>
                                    )}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className={styles.indexCell}>
                                <Button
                                  kind="danger--ghost"
                                  size="sm"
                                  hasIconOnly
                                  renderIcon={TrashCan}
                                  iconDescription="Delete operation"
                                  tooltipPosition="left"
                                  onClick={() => removeOp(i)}
                                />
                              </TableCell>
                            </SortableOpRow>
                          ))}
                        </SortableContext>
                      </TableBody>
                    </Table>
                  </DndContext>
                </TableContainer>
              ) : (
                <div className={styles.empty}>
                  <p className={styles.emptyTitle}>No operations</p>
                  <p className={styles.emptyHint}>
                    Add an operation to start building this series.
                  </p>
                </div>
              )}
            </section>
          )}

          {visible.formula && (
            <section className={styles.card}>
              <h2 className={styles.cardTitle}>Combined Series Formulas</h2>
              <p className={styles.formulaSub}>
                Operations applied in order, split into each curve&apos;s
                distinct tenor windows.
              </p>
              <code className={styles.formulaCode}>
                {(() => {
                  const formulas = combinedFormulas(values.ops, curveLabel);
                  if (formulas.length === 0)
                    return <div className={styles.formulaLine}>base curve</div>;
                  return formulas.map((f, i) => (
                    <div key={i} className={styles.formulaGroup}>
                      <span className={styles.formulaCurve}>
                        {f.curveLabel}
                      </span>
                      <div className={styles.formulaLines}>
                        {f.segments.map((seg, j) => (
                          <div key={j} className={styles.formulaLine}>
                            = {seg}
                          </div>
                        ))}
                      </div>
                    </div>
                  ));
                })()}
              </code>
            </section>
          )}

          {!visible.operations && !visible.formula && (
            <div className={styles.allHidden}>
              <p className={styles.emptyTitle}>All sections hidden</p>
              <p className={styles.emptyHint}>
                Turn a section back on from Layout in the panel.
              </p>
            </div>
          )}
        </div>

        {!isNew && (
          <ActivityFeedPanel
            modelType="Pricing::Transformation"
            modelId={transformation.id}
            title="Activity"
            subtitle={values.name || 'Untitled'}
            commentsHref={`/pricing/transformations/${transformation.id}/comments`}
          />
        )}
      </div>
    </div>
  );
}
